import _ from 'lodash';
import { MoveStates, PostStatus, UNORGANIZED_TOPIC_ID } from '../constants';
import isTopicPostPinned from './isTopicPostPinned';
import type { ReorganizeOperation } from '../../graphql/generated/graphql';
import { ReorganizeOperationName } from '../../graphql/generated/graphql';
import type { TopicReorganizationTopicPost } from '../useTopicReorganization';

function getPostStatusOperations(
  status: PostStatus,
  postId: string,
): ReorganizeOperation | null {
  switch (status) {
    case PostStatus.deleted:
      return {
        operationName: ReorganizeOperationName.DeletePost,
        id: postId,
      };
    case PostStatus.published:
    case PostStatus.unpublished:
      return {
        operationName: ReorganizeOperationName.UpdatePost,
        id: postId,
        published: status === PostStatus.published,
      };
    case PostStatus.archived:
    case PostStatus.unarchived:
      return {
        operationName: ReorganizeOperationName.UpdatePost,
        id: postId,
        archived: status === PostStatus.archived,
      };
    default:
      return null;
  }
}

interface GetPostsOperationsArgs {
  parentId: string;
  topicPosts: TopicReorganizationTopicPost[];
}

const getPostsOperations = ({
  parentId,
  topicPosts,
}: GetPostsOperationsArgs): ReorganizeOperation[] => {
  const moveAndPinOperations = topicPosts.flatMap((topicPost, index) => {
    const operations: ReorganizeOperation[] = [];

    const {
      node: post,
      reorganizationMetadata: { move, isPinned },
    } = topicPost;

    const state = move?.state;

    if (state === MoveStates.movedOut && parentId !== UNORGANIZED_TOPIC_ID) {
      operations.push({
        operationName: ReorganizeOperationName.RemoveTopicFromPost,
        postId: post.id,
        topicId: parentId,
      });
    }

    if (state === MoveStates.movedIn) {
      operations.push({
        operationName: ReorganizeOperationName.AddTopicToPost,
        postId: post.id,
        topicId: parentId,
      });
    }

    if (state === MoveStates.initial && isPinned === false) {
      operations.push({
        operationName: ReorganizeOperationName.UnpinPost,
        postId: post.id,
        topicId: parentId,
      });
    }

    if (
      isTopicPostPinned(topicPost) &&
      (state === MoveStates.movedIn || index !== topicPost.pinnedOrder)
    ) {
      operations.push({
        operationName: ReorganizeOperationName.PinPost,
        postId: post.id,
        topicId: parentId,
        pinnedOrder: index,
      });
    }

    return operations;
  });

  const postStatusOperations = _.compact(
    topicPosts.map((topicPost) => {
      return getPostStatusOperations(
        topicPost.reorganizationMetadata.status,
        topicPost.node.id,
      );
    }),
  );

  return [...moveAndPinOperations, ...postStatusOperations];
};

export default getPostsOperations;
